
import React, { useState, useRef, useEffect } from 'react';
import { Send, BrainCircuit, Sparkles, User, Bot, Loader2, MessageSquareText } from 'lucide-react';
import { chatWithAi, registerAiConsent } from '../services/aiApi';

interface Message {
  id: string;
  role: 'user' | 'model';
  text: string;
  timestamp: Date;
  isError?: boolean;
}

interface ClinicalAssistantProps {
  patientId?: string;
}

const ClinicalAssistant: React.FC<ClinicalAssistantProps> = ({ patientId }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'model',
      text: 'Olá! Sou seu Assistente Clínico. Como posso ajudar em sua prática hoje? Podemos discutir um caso, revisar uma técnica ou explorar uma abordagem teórica.',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      text: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      if (!patientId) {
        throw new Error('Selecione ou cadastre um paciente para usar o chat clínico.');
      }
      const context = messages
        .slice(-8)
        .filter((m) => !m.isError)
        .map((m) => `${m.role === 'user' ? 'Psicólogo' : 'Assistente'}: ${m.text}`)
        .join('\n');

      await registerAiConsent(patientId);
      const aiResponse = await chatWithAi(patientId, userMessage.text, context);
      
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'model',
        text: aiResponse?.answer || 'Desculpe, tive um problema ao processar sua solicitação.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Erro no assistente:', error);
      const err = error as { message?: string };
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'model',
        text: 'Não consegui responder agora. Tente enviar novamente em alguns instantes.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, botMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-190px)] md:min-h-[calc(100vh-160px)] animate-in fade-in duration-500">
      {/* Header do Chat */}
      <div className="bg-white dark:bg-slate-900 p-4 md:p-6 rounded-t-[2rem] md:rounded-t-[2.5rem] border-x border-t border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between transition-colors">
        <div className="flex items-center gap-4">
          <div className="bg-indigo-600 p-3 rounded-2xl text-white shadow-lg shadow-indigo-100">
            <BrainCircuit size={24} />
          </div>
          <div>
            <h3 className="text-lg font-black text-slate-800 dark:text-slate-100">Supervisor IA On-Demand</h3>
            <p className="text-[10px] text-indigo-500 dark:text-indigo-400 font-bold uppercase tracking-widest">OpenAI • Supervisão Clínica</p>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <Sparkles size={14} className="text-amber-500" />
          <span className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Análise Teórica Avançada Ativa</span>
        </div>
      </div>

      {/* Área de Mensagens */}
      <div className="flex-1 bg-white dark:bg-slate-900 border-x border-slate-200 dark:border-slate-800 overflow-y-auto p-4 md:p-6 space-y-5 md:space-y-6 transition-colors">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 duration-300`}>
            <div className={`flex max-w-[92%] md:max-w-[80%] gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 shadow-sm border ${
                msg.role === 'user' ? 'bg-indigo-50 dark:bg-slate-800 border-indigo-100 dark:border-slate-700 text-indigo-600 dark:text-indigo-400' : 'bg-slate-900 border-slate-800 text-white'
              }`}>
                {msg.role === 'user' ? <User size={16} /> : <Bot size={16} />}
              </div>
              <div className={`p-4 rounded-2xl text-sm leading-relaxed shadow-sm ${
                msg.role === 'user' 
                  ? 'bg-indigo-600 text-white rounded-tr-none shadow-indigo-200/50' 
                  : 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-100 dark:border-slate-700 rounded-tl-none'
              }`}>
                <div className="whitespace-pre-wrap">{msg.text}</div>
                <div className={`text-[9px] mt-2 font-bold uppercase tracking-widest opacity-50 ${msg.role === 'user' ? 'text-right' : ''}`}>
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start animate-pulse">
            <div className="flex gap-3 max-w-[92%] md:max-w-[80%]">
              <div className="w-8 h-8 rounded-xl bg-slate-900 flex items-center justify-center text-white">
                <Loader2 size={16} className="animate-spin" />
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 p-4 rounded-2xl rounded-tl-none">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                  <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input de Mensagem */}
      <div className="p-4 md:p-6 bg-slate-50 dark:bg-slate-950 rounded-b-[2rem] md:rounded-b-[2.5rem] border-x border-b border-slate-200 dark:border-slate-800 transition-colors">
        <div className="relative group">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Descreva o caso ou dúvida teórica..."
            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-[1.5rem] p-4 pr-16 text-sm focus:ring-4 focus:ring-indigo-500/10 focus:outline-none focus:border-indigo-400 transition-all shadow-inner dark:text-slate-200 min-h-[60px] max-h-[150px] resize-none"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="absolute right-3 bottom-3 p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none disabled:opacity-50 disabled:shadow-none active:scale-95"
          >
            <Send size={18} />
          </button>
        </div>
        <p className="text-[9px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest text-center mt-4">
          Esta IA não substitui a supervisão clínica humana obrigatória. Mantenha o sigilo dos pacientes.
        </p>
      </div>
    </div>
  );
};

export default ClinicalAssistant;
